import { impactsData, cityDataAvailable, sectorPriorityWeights, indicatorsWithData } from '../data/impactsData';
import { fetchWardSeverityDistribution, calculateIndicatorStatus } from './indicatorStatusUtils';
import { getDefaultThreshold } from './severityUtils';

// Cache for indicator statuses (key: `${city}_${indicatorName}`)
const indicatorStatusCache = {};

// Get threshold from localStorage (must match DashboardContext so map and status use same value)
const getIndicatorThreshold = (indicatorName) => {
  const saved = localStorage.getItem(`threshold_${indicatorName}`);
  if (saved) {
    try {
      const parsed = JSON.parse(saved);
      if (indicatorName === 'Groundwater stress levels' && typeof parsed === 'number' && parsed === 9000) {
        localStorage.setItem(`threshold_${indicatorName}`, '0.5');
        return 0.5;
      }
      return parsed;
    } catch (e) {
      const parsedValue = parseFloat(saved);
      if (!isNaN(parsedValue)) {
        if (indicatorName === 'Groundwater stress levels' && parsedValue === 9000) {
          localStorage.setItem(`threshold_${indicatorName}`, '0.5');
          return 0.5;
        }
        return parsedValue;
      }
    }
  }
  return getDefaultThreshold(indicatorName);
};

// Check if indicator has data
export const hasIndicatorData = (city, indicatorName) => {
  if (!indicatorsWithData[city]) return false;
  return indicatorsWithData[city].includes(indicatorName);
};

// Calculate sector priority score
export const calculateSectorPriority = (sectorName, impactCategory, city) => {
  const weight = sectorPriorityWeights[sectorName] || 5;
  
  let sectorData = null;
  let impactData = null;
  
  for (const impact of impactsData) {
    if (impact.id === impactCategory) {
      impactData = impact;
      sectorData = impact.sectors.find(s => s.name === sectorName);
      break;
    }
  }
  
  if (!sectorData || !impactData) return { score: 0, status: 'normal', indicators: [] };
  
  let normalIndicators = [];
  let warningIndicators = [];
  let highIndicators = [];
  
  const isDataAvailable = cityDataAvailable[city];
  if (isDataAvailable) {
    sectorData.indicators.forEach(ind => {
      if (!hasIndicatorData(city, ind.name)) return;
      
      // Get status from cache (should be populated by fetchIndicatorStatus)
      const status = getIndicatorStatus(ind.name, city);
      
      if (status === 'high') {
        highIndicators.push(ind.name);
      } else if (status === 'warning') {
        warningIndicators.push(ind.name);
      } else if (status === 'normal') {
        normalIndicators.push(ind.name);
      }
      // If status is null, indicator is not included in any category
    });
  }
  
  let severityMultiplier = 0;
  if (highIndicators.length > 0) {
    severityMultiplier = 3.0;
  } else if (warningIndicators.length > 0) {
    severityMultiplier = 2.0;
  } else if (normalIndicators.length > 0) {
    severityMultiplier = 1.0;
  }
  
  const priorityScore = weight * severityMultiplier;
  
  let status = 'normal';
  if (highIndicators.length > 0) status = 'high';
  else if (warningIndicators.length > 0) status = 'warning';
  
  return {
    score: priorityScore,
    status: status,
    weight: weight,
    normalCount: normalIndicators.length,
    warningCount: warningIndicators.length,
    highCount: highIndicators.length,
    totalIndicators: normalIndicators.length + warningIndicators.length + highIndicators.length,
    impactCategory: impactData.title,
    impactId: impactCategory
  };
};

// Get all sectors with priorities
export const getAllSectorPriorities = (city) => {
  const sectors = [];
  
  impactsData.forEach(impact => {
    impact.sectors.forEach(sector => {
      const priority = calculateSectorPriority(sector.name, impact.id, city);
      if (priority.totalIndicators > 0) {
        sectors.push({
          name: sector.name,
          impactCategory: impact.title,
          impactId: impact.id,
          priority: priority
        });
      }
    });
  });
  
  sectors.sort((a, b) => b.priority.score - a.priority.score);
  
  return sectors;
};

// Calculate indicator status breakdown for an impact
// Uses cached statuses from fetchIndicatorStatus
export const calculateIndicatorStatusBreakdown = (impact, city, hasIndicatorDataFn = null) => {
  // Use provided function or fall back to static version
  const checkHasData = hasIndicatorDataFn || hasIndicatorData;
  
  let totalIndicators = 0;
  let indicatorsWithDataCount = 0;
  let allIndicatorNames = [];
  let availableIndicatorNames = [];
  let normalIndicators = [];
  let warningIndicators = [];
  let highIndicators = [];
  
  impact.sectors.forEach(sector => {
    sector.indicators.forEach(ind => {
      totalIndicators++;
      allIndicatorNames.push(ind.name);
      const hasData = checkHasData(city, ind.name);
      
      if (hasData) {
        indicatorsWithDataCount++;
        availableIndicatorNames.push(ind.name);
        
        // Get status from cache (should be populated by fetchIndicatorStatus)
        const status = getIndicatorStatus(ind.name, city);
        
        console.log(`[BREAKDOWN] Indicator: ${ind.name} | Status: ${status || 'null'} | HasData: ${hasData}`);
        
        if (status === 'high') {
          highIndicators.push(ind.name);
        } else if (status === 'warning') {
          warningIndicators.push(ind.name);
        } else if (status === 'normal') {
          normalIndicators.push(ind.name);
        }
        // If status is null, indicator is not included in any category
      }
    });
  });
  
  let cardBorderColor;
  if (indicatorsWithDataCount === 0) {
    cardBorderColor = 'disabled';
  } else if (highIndicators.length > 0) {
    cardBorderColor = 'high';
  } else if (warningIndicators.length > 0) {
    cardBorderColor = 'warning';
  } else {
    cardBorderColor = 'normal';
  }
  
  const breakdown = {
    totalIndicators,
    indicatorsWithData: indicatorsWithDataCount,
    normalCount: normalIndicators.length,
    warningCount: warningIndicators.length,
    highCount: highIndicators.length,
    cardBorderColor,
    allIndicatorNames,
    availableIndicatorNames,
    normalIndicators,
    warningIndicators,
    highIndicators
  };
  
  const impactName = impact.title || impact.name || impact.id || 'Unknown Impact';
  console.log(`[BREAKDOWN] Summary for ${impactName}: Normal: ${breakdown.normalCount}, Warning: ${breakdown.warningCount}, High: ${breakdown.highCount}`);
  
  return breakdown;
};

/**
 * Get status for a single indicator (normal, warning, high)
 * Uses cached status if available, otherwise returns null (status should be fetched async)
 * 
 * @param {string} indicatorName - Name of the indicator
 * @param {string} city - City name
 * @returns {string|null} - 'normal', 'warning', 'high', or null
 */
export const getIndicatorStatus = (indicatorName, city) => {
  if (!hasIndicatorData(city, indicatorName)) {
    return null; // No data, no status
  }
  
  // Return cached status if available
  const cacheKey = `${city}_${indicatorName}`;
  if (indicatorStatusCache[cacheKey] !== undefined) {
    const status = indicatorStatusCache[cacheKey];
    console.log(`[STATUS] 📊 getIndicatorStatus: ${indicatorName} | City: ${city} | Status: ${status || 'null'}`);
    return status;
  }
  
  // Return null if not cached (status should be fetched async)
  console.log(`[STATUS] ⏳ getIndicatorStatus: ${indicatorName} | City: ${city} | Status: NOT CACHED YET`);
  return null;
};

/**
 * Fetch and calculate indicator status based on ward severity distribution
 * This is the async version that should be called to populate the cache
 * Includes timeout to prevent blocking UI
 * 
 * @param {string} indicatorName - Name of the indicator
 * @param {string} city - City name
 * @param {Date} startDate - Optional start date (defaults to current month)
 * @param {Date} endDate - Optional end date (defaults to current month)
 * @returns {Promise<string|null>} - 'normal', 'warning', 'high', or null
 */
export const fetchIndicatorStatus = async (indicatorName, city, startDate = null, endDate = null) => {
  if (!hasIndicatorData(city, indicatorName)) {
    return null;
  }
  
  const cacheKey = `${city}_${indicatorName}`;
  
  // Check cache first - if already fetched, return immediately
  if (indicatorStatusCache[cacheKey] !== undefined) {
    console.log(`[STATUS] 💾 CACHED: ${indicatorName} | City: ${city} | Status: ${indicatorStatusCache[cacheKey] || 'null'}`);
    return indicatorStatusCache[cacheKey];
  }
  
  const threshold = getIndicatorThreshold(indicatorName);
  const startTime = Date.now();
  
  console.log(`[STATUS] 🌐 FETCHING: ${indicatorName} | City: ${city} | Threshold: ${threshold}`);
  
  // Add timeout to prevent blocking (10 seconds)
  const timeoutPromise = new Promise((_, reject) => {
    setTimeout(() => reject(new Error('Status fetch timeout')), 10000);
  });
  
  try {
    const fetchPromise = (async () => {
      const severityDistribution = await fetchWardSeverityDistribution(
        indicatorName,
        city,
        threshold,
        startDate,
        endDate
      );
      
      if (!severityDistribution) {
        // No ward-level data available for this indicator
        const duration = Date.now() - startTime;
        console.warn(`[STATUS] ⚠️  NO DATA: ${indicatorName} | City: ${city} | Duration: ${duration}ms | No ward-level data available`);
        indicatorStatusCache[cacheKey] = null;
        return null;
      }
      
      const status = calculateIndicatorStatus(severityDistribution);
      const duration = Date.now() - startTime;
      console.log(`[STATUS] ✅ COMPLETE: ${indicatorName} | City: ${city} | Status: ${status || 'null'} | Duration: ${duration}ms`);
      indicatorStatusCache[cacheKey] = status;
      return status;
    })();
    
    // Race between fetch and timeout
    const status = await Promise.race([fetchPromise, timeoutPromise]);
    return status;
  } catch (error) {
    const duration = Date.now() - startTime;
    // On timeout or error, don't cache null immediately - allow retry
    if (error.message.includes('timeout')) {
      console.error(`[STATUS] ⏱️  TIMEOUT: ${indicatorName} | City: ${city} | Duration: ${duration}ms | API call exceeded 10 second timeout`);
      console.error(`[STATUS] This may indicate slow API response or database connection issues`);
    } else {
      console.error(`[STATUS] 🔴 ERROR: ${indicatorName} | City: ${city} | Duration: ${duration}ms | Error: ${error.message}`);
    }
    // Don't cache null on error - allow retry on next render
    return null;
  }
};

/**
 * Clear the indicator status cache (useful when thresholds change or city changes)
 */
export const clearIndicatorStatusCache = () => {
  Object.keys(indicatorStatusCache).forEach(key => {
    delete indicatorStatusCache[key];
  });
};

